import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from 'react';

/**
 * DSGVO-Consent-Context.
 *
 * Hält den aktuellen Einwilligungsstatus + Server-Tracking-Konfiguration und
 * verteilt beides per `useConsent()` an Cookie-Banner, Tracking-Loader und
 * Footer-Link zum Wiederöffnen.
 *
 * Format des persistierten Consent-Objekts (localStorage `smartvolt_consent`):
 *   {
 *     version: number,          // entspricht config.consentVersion zum Zeitpunkt des Speicherns
 *     timestamp: ISO,           // wann zugestimmt
 *     categories: {
 *       essential: true,        // immer true
 *       statistik: bool,        // GA
 *       marketing: bool         // Google Ads + Meta + LinkedIn + TikTok
 *     }
 *   }
 */

const STORAGE_KEY = 'smartvolt_consent';
const API = process.env.REACT_APP_BACKEND_URL;

const ConsentContext = createContext(null);

const DEFAULT_CATEGORIES = {
  essential: true,
  statistik: false,
  marketing: false };

const loadFromStorage = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    return parsed;
  } catch {
    return null;
  }
};

const saveToStorage = (consent) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(consent));
  } catch {
    /* ignore quota errors */
  }
};

export const ConsentProvider = ({ children }) => {
  const [config, setConfig] = useState(null);
  const [consent, setConsent] = useState(loadFromStorage);
  const [bannerOpen, setBannerOpen] = useState(false);
  const [loaded, setLoaded] = useState(false);

  // Server-Konfiguration laden
  useEffect(() => {
    let cancelled = false;
    fetch(`${API}/api/tracking/config`)
      .then((r) => r.json())
      .then((d) => {
        if (cancelled) return;
        if (d.success) setConfig(d.config);
      })
      .catch(() => { /* offline – Banner zeigt trotzdem essentielle Cookies */ })
      .finally(() => { if (!cancelled) setLoaded(true); });
    return () => { cancelled = true; };
  }, []);

  // Sobald Config geladen: prüfen, ob bestehender Consent noch gilt
  useEffect(() => {
    if (!loaded) return;
    const currentVersion = Number((config && config.consentVersion) || 1);
    if (!consent || Number(consent.version) !== currentVersion) {
      setBannerOpen(true);
    }
  }, [loaded, config, consent]);

  const saveConsent = useCallback((categories) => {
    const next = {
      version: Number((config && config.consentVersion) || 1),
      timestamp: new Date().toISOString(),
      categories: {
        essential: true,
        statistik: !!categories?.statistik,
        marketing: !!categories?.marketing } };
    saveToStorage(next);
    setConsent(next);
    setBannerOpen(false);
  }, [config]);

  const acceptAll = useCallback(() => saveConsent({ statistik: true, marketing: true }), [saveConsent]);
  const rejectAll = useCallback(() => saveConsent({ statistik: false, marketing: false }), [saveConsent]);
  const openBanner = useCallback(() => setBannerOpen(true), []);

  const categories = consent?.categories || DEFAULT_CATEGORIES;

  // Welche konkreten Tracker dürfen aktiv werden?
  const activeTrackers = useMemo(() => {
    if (!config) return {};
    const ga = config.googleAds;
    const an = config.googleAnalytics;
    const mp = config.metaPixel;
    const li = config.linkedInInsight;
    const tk = config.tiktokPixel;
    return {
      googleAds: !!(ga?.enabled && ga?.conversionId && categories.marketing),
      googleAnalytics: !!(an?.enabled && an?.measurementId && categories.statistik),
      metaPixel: !!(mp?.enabled && mp?.pixelId && categories.marketing),
      linkedInInsight: !!(li?.enabled && li?.partnerId && categories.marketing),
      tiktokPixel: !!(tk?.enabled && tk?.pixelId && categories.marketing) };
  }, [config, categories]);

  const value = useMemo(() => ({
    loaded,
    config,
    consent,
    categories,
    activeTrackers,
    bannerOpen,
    saveConsent,
    acceptAll,
    rejectAll,
    openBanner }), [loaded, config, consent, categories, activeTrackers, bannerOpen, saveConsent, acceptAll, rejectAll, openBanner]);

  return <ConsentContext.Provider value={value}>{children}</ConsentContext.Provider>;
};

export const useConsent = () => {
  const ctx = useContext(ConsentContext);
  if (!ctx) {
    // Defensive Fallback (z. B. wenn der Banner außerhalb des Providers gerendert wird)
    return {
      loaded: false, config: null, consent: null,
      categories: DEFAULT_CATEGORIES, activeTrackers: {},
      bannerOpen: false,
      saveConsent: () => {}, acceptAll: () => {}, rejectAll: () => {}, openBanner: () => {} };
  }
  return ctx;
};
